import { useEffect, forwardRef, useImperativeHandle, useRef, useState, useMemo } from "react";
import type { UIMessage } from "ai";
import { AIMessage } from "./AIMessage";
import { ThinkingIndicator } from "./ThinkingIndicator";
import { ImageLoadingSkeleton } from "./ImageLoadingSkeleton";
import { cn } from "@/lib/utils";
import { ImageLightbox } from "@/components/ui/image-lightbox";

interface AIMessagesProps {
  messages: UIMessage[];
  currentModel?: string;
  isPending?: boolean;
  chatId?: string;
  scrollContainer?: HTMLElement | null;
  onRetry?: (content: string, model: string) => void;
  onModelChange?: (model: string) => void;
  showReservedSpace: boolean;
  isOwner: boolean;
  imageMode?: boolean;
}

export interface AIMessagesRef {
  scrollToBottom: (smooth?: boolean) => void;
  scrollToIndex: (
    index: number,
    options?: { align?: "start" | "center" | "end" | "auto"; smooth?: boolean },
  ) => void;
}

export const AIMessages = forwardRef<AIMessagesRef, AIMessagesProps>(
  (
    {
      messages,
      currentModel,
      isPending,
      chatId,
      scrollContainer,
      onRetry,
      onModelChange,
      showReservedSpace,
      isOwner,
      imageMode = false,
    },
    ref,
  ) => {
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // Collect all images from messages
    const allImages = useMemo(() => {
      const images: { src: string; alt: string }[] = [];
      messages.forEach((message) => {
        message.parts?.forEach((part) => {
          if (part.type === "image") {
            images.push({
              src: part.url,
              alt: "Generated image",
            });
          }
        });
      });
      return images;
    }, [messages]);

    const handleImageClick = (imageUrl: string) => {
      const imageIndex = allImages.findIndex((img) => img.src === imageUrl);
      if (imageIndex !== -1) {
        setCurrentImageIndex(imageIndex);
        setLightboxOpen(true);
      }
    };

    const scrollToBottom = () => {
      if (!scrollContainer) return;

      let lastScrollTop = -1;
      const attemptScroll = () => {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;

        // If scroll position changed, try again next frame
        if (scrollContainer.scrollTop !== lastScrollTop) {
          lastScrollTop = scrollContainer.scrollTop;
          requestAnimationFrame(attemptScroll);
        }
      };

      attemptScroll();
    };

    useImperativeHandle(ref, () => ({
      scrollToBottom,
      scrollToIndex: (index, options) => {
        const messageElements = messagesContainerRef.current?.children;
        if (messageElements && messageElements[index]) {
          messageElements[index].scrollIntoView({
            behavior: options?.smooth ? "smooth" : "auto",
            block: options?.align || "nearest",
          });
        }
      },
    }));

    // Track if we've scrolled for this chat
    const hasScrolledRef = useRef<string | null>(null);

    // Auto-scroll to bottom when chat loads or switches
    useEffect(() => {
      if (chatId && hasScrolledRef.current !== chatId && scrollContainer && messages.length > 0) {
        // Use requestAnimationFrame to ensure DOM is updated
        requestAnimationFrame(() => {
          scrollToBottom();
          hasScrolledRef.current = chatId;
        });
      }
    }, [chatId, scrollContainer, messages.length]);

    return (
      <div className="pt-[57px] pb-[160px]">
        <div className="max-w-4xl mx-auto p-4 select-none">
          <div ref={messagesContainerRef}>
            {messages.map((message, index) => {
              const isLastMessage = index === messages.length - 1;
              return (
                <div
                  key={message.id}
                  className={cn("py-1", {
                    "min-h-[calc(100vh-21rem)]": showReservedSpace && isLastMessage,
                  })}
                >
                  <AIMessage
                    message={message as any}
                    messages={messages}
                    currentModel={currentModel}
                    onRetry={onRetry}
                    onModelChange={onModelChange}
                    isOwner={isOwner}
                    imageMode={imageMode}
                    onImageClick={handleImageClick}
                  />
                  {isPending && isLastMessage && (
                    <div className="mt-4 px-4">
                      {imageMode ? <ImageLoadingSkeleton /> : <ThinkingIndicator />}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Image Lightbox */}
        <ImageLightbox
          images={allImages}
          currentIndex={currentImageIndex}
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          onNavigate={setCurrentImageIndex}
        />
      </div>
    );
  },
);

AIMessages.displayName = "AIMessages";
